import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from 'react';

/** Pixels from the bottom of the scroll area to still count as “at bottom”. */
const PIN_DISTANCE_PX = 48;

function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node) {
    const { overflow, overflowY } = getComputedStyle(node);
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll'
    ) {
      return node;
    }

    node = node.parentElement;
  }
  return null;
}

function isPinnedToBottom(el: HTMLElement): boolean {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - PIN_DISTANCE_PX;
}

/**
 * Scrolls the {@link MessageList} end sentinel into view when `when` updates
 * (e.g. the displayed messages array). Used on the dashboard home feed where
 * the composer sits in a fixed footer.
 *
 * When the scroll container resizes (e.g. footer portal mounts or the footer
 * shell becomes visible), re-scrolls only if the user was already pinned to
 * the bottom, so window resizes or late layout do not yank the viewport when
 * reading older messages.
 */
export function useScrollMessageListToEnd(
  messagesEndRef: RefObject<HTMLDivElement | null>,
  when: unknown,
): void {
  const pinnedToBottomRef = useRef(true);

  const scrollToEnd = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messagesEndRef]);

  useLayoutEffect(() => {
    scrollToEnd();
    pinnedToBottomRef.current = true;
  }, [when, scrollToEnd]);

  useEffect(() => {
    const sentinel = messagesEndRef.current;
    if (!sentinel) return;

    const scrollParent = getScrollableAncestor(sentinel);
    if (!scrollParent) return;

    const updatePinned = () => {
      pinnedToBottomRef.current = isPinnedToBottom(scrollParent);
    };

    scrollParent.addEventListener('scroll', updatePinned, { passive: true });
    updatePinned();

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!pinnedToBottomRef.current) return;

        scrollToEnd();
        pinnedToBottomRef.current = isPinnedToBottom(scrollParent);
      });
    });
    ro.observe(scrollParent);

    return () => {
      scrollParent.removeEventListener('scroll', updatePinned);
      ro.disconnect();
    };
  }, [when, messagesEndRef, scrollToEnd]);
}
